import React, { useState, useRef, useEffect, createRef } from "react"
import PropTypes from "prop-types"
import { Flex, Button, Radio, Tooltip } from "antd"
import { DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { Modal } from "ui/Modal"
import { ConfirmModal } from "ui/ConfirmModal"
import SeasonForm from "./components/SeasonForm"
import { AVAILABILITY_TYPES } from "./constants"
import styles from "./index.module.css"

/**
 * Unified AvailabilityModal component for adding and editing crew member availability
 */
const AvailabilityModal = ({ open, onCancel, onSave, mode = "add", initialData = null, loading = false, validationErrors = {} }) => {
  const { t } = useTranslation()
  const scrollRef = useRef(null)

  // Initialize seasons based on mode
  const getInitialSeasons = () => {
    if (mode === "edit" && initialData) {
      return initialData
    }
    return [{ id: 1, number: 1 }]
  }

  // Determine initial availability type from data
  const getInitialAvailabilityType = () => {
    if (mode === "edit" && initialData && initialData.length > 0) {
      const firstAvailability = initialData[0]
      // Check all_year field - if false, it's seasonal
      if (firstAvailability?.data?.all_year === false) {
        return AVAILABILITY_TYPES.SEASONAL
      }
      // Check if all_year is true
      if (firstAvailability?.data?.all_year === true) {
        return AVAILABILITY_TYPES.ALL_YEAR
      }
    }
    // Default to ALL_YEAR for new availability (as per requirement)
    return AVAILABILITY_TYPES.ALL_YEAR
  }

  const [seasons, setSeasons] = useState(getInitialSeasons())
  const initialSeasonsRef = useRef(getInitialSeasons())
  const seasonFormRefs = useRef([])
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [seasonToDelete, setSeasonToDelete] = useState(null)
  const [deletedSeasonIds, setDeletedSeasonIds] = useState([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [modalOpenCount, setModalOpenCount] = useState(0)
  const [availabilityType, setAvailabilityType] = useState(getInitialAvailabilityType())

  // Update seasons when modal opens with new data
  useEffect(() => {
    if (open) {
      const newSeasons = getInitialSeasons()
      setSeasons(newSeasons)
      initialSeasonsRef.current = newSeasons
      // Initialize refs array for each season
      seasonFormRefs.current = newSeasons.map((_, i) => seasonFormRefs.current[i] || createRef())
      // Reset deleted seasons tracking
      setDeletedSeasonIds([])
      // Reset unsaved changes flag
      setHasUnsavedChanges(false)
      // Increment counter to force remount of SeasonForm components
      setModalOpenCount(prev => prev + 1)
      // Set availability type based on data
      setAvailabilityType(getInitialAvailabilityType())
    }
  }, [open, mode, initialData])

  // Check if any changes have been made
  const checkForChanges = () => {
    // Check if availability type changed
    const initialType = getInitialAvailabilityType()
    if (availabilityType !== initialType) {
      setHasUnsavedChanges(true)
      return
    }

    // Check if seasons were added
    if (seasons.length !== initialSeasonsRef.current.length) {
      setHasUnsavedChanges(true)
      return
    }

    // Check if any seasons were deleted
    if (deletedSeasonIds.length > 0) {
      setHasUnsavedChanges(true)
      return
    }

    // Check if any season form data has changed
    let hasFormChanges = false
    seasons.forEach((_, index) => {
      const formRef = seasonFormRefs.current[index]
      if (formRef?.current?.hasChanges) {
        if (formRef.current.hasChanges()) {
          hasFormChanges = true
        }
      }
    })

    setHasUnsavedChanges(hasFormChanges)
  }

  // Check for changes whenever relevant state updates
  useEffect(() => {
    if (open && mode === "edit") {
      checkForChanges()
    }
  }, [seasons, deletedSeasonIds, availabilityType])

  const handleAvailabilityTypeChange = (e) => {
    const newType = e.target.value
    setAvailabilityType(newType)

    // When switching to ALL_YEAR, ensure we have at least one season
    // When switching to SEASONAL, ensure we have at least one season
    // Keep existing data to preserve user input
    if (seasons.length === 0) {
      setSeasons([{ id: 1, number: 1 }])
      seasonFormRefs.current = [createRef()]
    }
  }

  const handleAddSeason = () => {
    const newSeasonNumber = seasons.length + 1
    setSeasons([...seasons, { id: Date.now(), number: newSeasonNumber }])

    // Add a new ref for the new season
    seasonFormRefs.current = [...seasonFormRefs.current, createRef()]

    // Mark as having unsaved changes
    setHasUnsavedChanges(true)

    // Scroll to bottom after new season is rendered
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight
      }
    }, 100)
  }

  const handleDeleteClick = (seasonId, seasonNumber) => {
    // Ensure at least one season always exists
    if (seasons.length <= 1) {
      return
    }

    setSeasonToDelete({ id: seasonId, number: seasonNumber })
    setIsDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (!seasonToDelete) return

    // Track deleted season if it has a database ID
    const seasonBeingDeleted = seasons.find((s) => s.id === seasonToDelete.id)
    if (seasonBeingDeleted?.data?.id) {
      setDeletedSeasonIds([...deletedSeasonIds, seasonBeingDeleted.data.id])
    }

    const updatedSeasons = seasons.filter((season) => season.id !== seasonToDelete.id)

    // Renumber remaining seasons
    const renumberedSeasons = updatedSeasons.map((season, index) => ({
      ...season,
      number: index + 1
    }))

    setSeasons(renumberedSeasons)
    // Update refs array to match new seasons length
    seasonFormRefs.current = renumberedSeasons.map((_, i) => seasonFormRefs.current[i] || createRef())
    setIsDeleteConfirmOpen(false)
    setSeasonToDelete(null)
    // Mark as having unsaved changes
    setHasUnsavedChanges(true)
  }

  const handleCancelDelete = () => {
    setIsDeleteConfirmOpen(false)
    setSeasonToDelete(null)
  }

  const handleSaveChanges = () => {
    // Validate all season forms first
    let allValid = true
    seasons?.forEach((_, index) => {
      const formRef = seasonFormRefs?.current[index]
      if (formRef?.current?.validate) {
        const isValid = formRef.current.validate()
        if (!isValid) {
          allValid = false
        }
      }
    })

    // If validation fails, don't proceed with save
    if (!allValid) {
      return
    }

    const availabilitiesAttributes = []

    // Add deleted seasons with _destroy flag
    deletedSeasonIds.forEach((id) => {
      availabilitiesAttributes.push({
        id,
        _destroy: true
      })
    })

    // Collect data from each season form
    seasons.forEach((season, index) => {
      const formRef = seasonFormRefs.current[index]
      if (formRef?.current) {
        const formData = formRef.current.getData()

        // Check if this is an existing availability (has database ID) or new one
        if (season.data?.id) {
          // Existing availability - include ID with all fields
          availabilitiesAttributes.push({
            id: season.data.id,
            ...formData
          })
        } else {
          // New availability - just the data, no ID
          availabilitiesAttributes.push(formData)
        }
      }
    })

    // Call onSave with the formatted data
    onSave({ availabilities_attributes: availabilitiesAttributes })
  }

  const handleCancel = () => {
    if (mode === "add") {
      // Reset to initial blank state for add mode
      setSeasons([{ id: 1, number: 1 }])
      setAvailabilityType(AVAILABILITY_TYPES.ALL_YEAR)
    } else {
      // Revert to original data for edit mode
      setSeasons(initialSeasonsRef.current)
      setAvailabilityType(getInitialAvailabilityType())
    }
    // Reset deleted seasons tracking
    setDeletedSeasonIds([])
    onCancel()
  }

  const modalTitle = mode === "edit"
    ? t("crewMember.availability.editTitle")
    : t("crewMember.availability.addTitle")

  return (
    <>
      <Modal
        open={open}
        onCancel={handleCancel}
        title={modalTitle}
        width={800}
        className={styles.modal}
        centered
        footer={null}
      >
        <Flex vertical className={styles.modalContainer}>
          {/* Scrollable Content */}
          <Flex vertical gap={24} className={styles.modalContent} ref={scrollRef}>
            {/* Availability Type Radio Group */}
            <Flex vertical gap={12}>
              <Radio.Group
                value={availabilityType}
                onChange={handleAvailabilityTypeChange}
                className={styles.radioGroup}
              >
                <Radio value={AVAILABILITY_TYPES.ALL_YEAR}>
                  {t("crewMember.availability.allYear", { defaultValue: "All Year" })}
                </Radio>
                <Radio value={AVAILABILITY_TYPES.SEASONAL}>
                  <Flex align="center" gap={6}>
                    {t("crewMember.availability.seasonal", { defaultValue: "Seasonal" })}
                    <Tooltip title={t("crewMember.availability.seasonalTooltip", { defaultValue: "Set specific date ranges when this crew member is available" })}>
                      <InfoCircleOutlined style={{ fontSize: 14, color: "var(--color-primary)" }} />
                    </Tooltip>
                  </Flex>
                </Radio>
              </Radio.Group>
            </Flex>

            {/* Season Forms */}
            <Flex vertical gap={32}>
              {availabilityType === AVAILABILITY_TYPES.ALL_YEAR ? (
                // Show single season form for all year
                <SeasonForm
                  key={`all-year-${modalOpenCount}`}
                  ref={seasonFormRefs.current[0]}
                  seasonNumber={1}
                  showSeasonTitle={false}
                  seasonData={seasons[0]?.data}
                  onFormChange={checkForChanges}
                  isAllYear={true}
                  serverError={validationErrors["0"]?.base?.[0] || null}
                />
              ) : (
                // Show multiple season forms for seasonal
                seasons?.map((season, index) => {
                  const isLastSeason = index === seasons.length - 1
                  // Get error for this specific season index
                  const seasonError = validationErrors[String(index)]?.base?.[0] || null

                  return (
                    <Flex key={season.id} vertical gap={16}>
                      {/* Season Header with Delete Button */}
                      {seasons.length > 1 && (
                        <Flex justify="space-between" align="center">
                          <div className={styles.seasonHeader}>
                            <span className={styles.seasonTitle}>
                              {t("crewMember.availability.season", { number: season.number })}
                            </span>
                          </div>
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteClick(season.id, season.number)}
                            className={styles.deleteSeasonButton}
                          >
                            {t("crewMember.availability.buttons.deleteSeason")}
                          </Button>
                        </Flex>
                      )}

                      <div className={isLastSeason ? "" : styles.seasonFormWrapper}>
                        <SeasonForm
                          key={`${season.id}-${modalOpenCount}`}
                          ref={seasonFormRefs.current[index]}
                          seasonNumber={season.number}
                          showSeasonTitle={false}
                          seasonData={season.data}
                          onFormChange={checkForChanges}
                          isAllYear={false}
                          serverError={seasonError}
                        />
                      </div>
                    </Flex>
                  )
                })
              )}
            </Flex>
          </Flex>

          {/* Fixed Bottom Section */}
          <Flex vertical className={styles.fixedFooter}>
            {/* Add Seasonal Availability Button - Only show for seasonal availability */}
            {availabilityType === AVAILABILITY_TYPES.SEASONAL && (
              <Button
                type="link"
                onClick={handleAddSeason}
                className={styles.addSeasonButton}
              >
                + {t("crewMember.availability.buttons.addSeasonalAvailability")}
              </Button>
            )}

            {/* Footer Buttons */}
            <Flex gap={12} justify="flex-start" className={styles.modalFooter}>
              <Button
                onClick={handleCancel}
                variant="outlined"
                color="primary"
                size="large"
                className={styles.cancelButton}
              >
                {t("crewMember.availability.buttons.cancel")}
              </Button>
              <Button
                type="primary"
                onClick={handleSaveChanges}
                size="large"
                className={styles.saveButton}
                loading={loading}
                disabled={loading || (mode === "edit" && !hasUnsavedChanges)}
              >
                {t("crewMember.availability.buttons.saveChanges")}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={isDeleteConfirmOpen}
        title={t("crewMember.availability.deleteConfirmation.title")}
        info={t("crewMember.availability.deleteConfirmation.message", {
          seasonNumber: seasonToDelete?.number || ""
        })}
        onCancel={handleCancelDelete}
        onDelete={handleConfirmDelete}
        cancelLabel={t("crewMember.availability.buttons.cancel")}
        deleteLabel={t("crewMember.availability.buttons.delete")}
      />
    </>
  )
}

AvailabilityModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(["add", "edit"]),
  initialData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      number: PropTypes.number.isRequired,
      data: PropTypes.object
    })
  ),
  loading: PropTypes.bool,
  validationErrors: PropTypes.object
}

export default AvailabilityModal
